// MasterExcelToolbar — Feb 2026.
// A drop-in trio of buttons every Master page uses for Excel workflows:
//   • Import Template — downloads a blank .xlsx with the correct headers.
//   • Export Excel    — downloads all current rows in the same layout.
//   • Import Excel    — opens a dialog to upload a filled .xlsx.
//
// Props:
//   basePath      → API subpath, e.g. "/part-master" (no trailing slash).
//                   Endpoints expected: {basePath}/template, /export, /import
//   downloadName  → filename for the template download (defaults derived).
//   exportName    → filename for the export download.
//   entityLabel   → user-facing name, e.g. "Parts", "Materials".
//   onImportDone  → callback invoked after a successful import so the parent
//                   page can reload its data.
//   size          → "sm" | "md" – button size (default "md").
//
// The dialog shows insert/update/skip counts returned by the backend so the
// user knows exactly what changed.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { DownloadSimple, FileArrowUp, FileXls } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MasterExcelToolbar({
  basePath,
  downloadName,
  exportName,
  entityLabel = "Rows",
  onImportDone,
  size = "md",
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const clean = basePath.replace(/^\/+|\/+$/g, "");
  const tmplFile = downloadName || `${clean}_template.xlsx`;
  const expFile  = exportName   || `${clean}_export.xlsx`;

  const cls = size === "sm"
    ? "min-h-[34px] h-8 px-2.5 text-xs"
    : "min-h-[38px] h-9 px-3 text-xs";

  const downloadBlob = async (url, filename) => {
    const res = await axios.get(url, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleTemplate = async () => {
    try {
      await downloadBlob(`${API}/${clean}/template`, tmplFile);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const handleExport = async () => {
    try {
      await downloadBlob(`${API}/${clean}/export`, expFile);
      toast.success(`${entityLabel} exported`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const handleImport = async () => {
    if (!file) return toast.error("Choose an .xlsx file first");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/${clean}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      const { inserted = 0, updated = 0, skipped = 0, flagged = 0, imported } = res.data || {};
      // Some endpoints use `imported` (Defect); normalise the toast message.
      const ins = inserted || imported || 0;
      const flag = flagged > 0 ? ` · ${flagged} flagged for review` : "";
      toast.success(`Imported — ${ins} new · ${updated} updated${skipped ? ` · ${skipped} skipped` : ""}${flag}`);
      if (onImportDone) onImportDone(res.data);
    } catch (e) {
      toast.error("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`excel-toolbar-${clean}`}>
        <Button variant="outline" onClick={handleTemplate}
          className={`${cls} border-[#059669] text-[#059669] hover:bg-[#ECFDF5] gap-1`}
          data-testid={`${clean}-template-btn`}>
          <DownloadSimple size={14} /> Import Template
        </Button>
        <Button variant="outline" onClick={handleExport}
          className={`${cls} border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F3FF] gap-1`}
          data-testid={`${clean}-export-btn`}>
          <FileXls size={14} weight="fill" /> Export Excel
        </Button>
        <Button variant="outline" onClick={() => { setResult(null); setFile(null); setOpen(true); }}
          className={`${cls} border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF] gap-1`}
          data-testid={`${clean}-import-btn`}>
          <FileArrowUp size={14} /> Import Excel
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-[#1D2951]">
              Import {entityLabel} from Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <p className="text-xs text-[#64748B]">
              Not sure of the layout? Download the template first — its headers match this importer exactly.
              Existing rows are updated in place (matched on their business key), new rows are inserted.
            </p>
            <Button variant="outline" onClick={handleTemplate}
              className="w-full border-[#059669] text-[#059669] min-h-[38px] gap-2"
              data-testid={`${clean}-dialog-template-btn`}>
              <DownloadSimple size={14} /> Download Template
            </Button>

            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">Select .xlsx file</Label>
              <Input type="file" accept=".xlsx" onChange={e => setFile(e.target.files[0])}
                className="min-h-[38px] mt-1" data-testid={`${clean}-import-file`} />
            </div>

            {result && (
              <div className="rounded border border-[#BBF7D0] bg-[#F0FDF4] p-2.5 text-[11px] text-[#166534]">
                <div className="font-bold mb-0.5">Import summary</div>
                <div>Inserted: <b>{result.inserted ?? result.imported ?? 0}</b></div>
                {"updated" in result && <div>Updated: <b>{result.updated}</b></div>}
                {"skipped" in result && result.skipped > 0 && <div>Skipped: <b>{result.skipped}</b></div>}
                {typeof result.flagged === "number" && result.flagged > 0 && (
                  <div className="mt-1 text-[#9D174D] font-semibold">
                    Flagged for review: {result.flagged}
                    <div className="text-[10px] text-[#9D174D]/80 mt-0.5 font-normal">
                      Rows imported with mismatched Part / Machine / Operation are highlighted in pink. Click their <b>FIX</b> button to edit.
                    </div>
                  </div>
                )}
                {Array.isArray(result.errors) && result.errors.length > 0 && (
                  <>
                    <div className="mt-1 text-[#B91C1C] font-semibold">Errors: {result.errors.length}</div>
                    <div className="mt-1 max-h-40 overflow-auto rounded bg-white border border-[#FECACA] p-1.5 text-[10px] text-[#7F1D1D] leading-snug">
                      {result.errors.slice(0, 15).map((e, i) => (
                        <div key={i} className="border-b border-[#FEE2E2] last:border-b-0 py-0.5">
                          Row <b>{e.row ?? "?"}</b>: {e.reason || JSON.stringify(e)}
                        </div>
                      ))}
                      {result.errors.length > 15 && (
                        <div className="pt-1 text-[#B91C1C] italic">
                          …and {result.errors.length - 15} more (same pattern likely).
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            <Button onClick={handleImport} disabled={!file || busy}
              className="w-full bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px] gap-2"
              data-testid={`${clean}-import-submit-btn`}>
              <FileArrowUp size={14} /> {busy ? "Importing…" : "Import"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
